import { useEffect, useState } from "react";
import { ApiError, api, type ReceiptSummary } from "../api/client";
import { Pagination } from "../components/Pagination";
import { receiptDetailPath } from "../routes";

// TODO: cover with component tests once the dashboard adopts a React
// test harness. Behaviors to cover: list renders with mocked data,
// empty state shows when total=0, loading shows during fetch, error
// state renders on failed fetch, the 503 case shows the dedicated copy,
// pagination controls update the URL and refetch.

const PAGE_SIZE = 25;

type LoadState =
  | { kind: "loading" }
  | {
      kind: "ready";
      receipts: ReceiptSummary[];
      total: number;
    }
  | { kind: "error"; status: number | null; code: string; message: string };

function readPageFromUrl(): number {
  const params = new URLSearchParams(window.location.search);
  const raw = params.get("page");
  if (!raw) return 1;
  const parsed = Number(raw);
  if (!Number.isFinite(parsed) || parsed < 1) return 1;
  return Math.floor(parsed);
}

function writePageToUrl(page: number): void {
  const url = new URL(window.location.href);
  if (page <= 1) {
    url.searchParams.delete("page");
  } else {
    url.searchParams.set("page", String(page));
  }
  window.history.pushState({}, "", url.toString());
}

export function ReceiptsPage(props: { onOpenDetail: (id: string) => void }) {
  const [page, setPage] = useState<number>(() => readPageFromUrl());
  const [state, setState] = useState<LoadState>({ kind: "loading" });

  // Keep the page state in sync with browser back / forward. The
  // numbered pagination component pushes new entries; popstate
  // restores the previous one.
  useEffect(() => {
    function onPopState() {
      setPage(readPageFromUrl());
    }
    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, []);

  useEffect(() => {
    let cancelled = false;
    setState({ kind: "loading" });
    (async () => {
      try {
        const response = await api.listReceipts({ page, pageSize: PAGE_SIZE });
        if (cancelled) return;
        setState({
          kind: "ready",
          receipts: response.receipts,
          total: response.total
        });
      } catch (err) {
        if (cancelled) return;
        if (err instanceof ApiError) {
          setState({
            kind: "error",
            status: err.status,
            code: err.code,
            message: err.message
          });
          return;
        }
        const message = err instanceof Error ? err.message : "Could not load receipts.";
        setState({ kind: "error", status: null, code: "network_error", message });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [page]);

  function changePage(next: number) {
    if (next === page) return;
    writePageToUrl(next);
    setPage(next);
  }

  return (
    <div className="panel">
      <div className="section-header">
        <h2 className="section-title">Receipts</h2>
        {state.kind === "ready" ? (
          <span className="section-meta">
            {state.total === 0 ? "No receipts yet" : `${state.total.toLocaleString()} total`}
          </span>
        ) : null}
      </div>
      <p className="muted" style={{ marginTop: 0 }}>
        Receipts your SDK has produced in managed mode. Click a row to view the full envelope and
        verification summary.
      </p>
      {renderBody(state, page, props.onOpenDetail, changePage)}
    </div>
  );
}

function renderBody(
  state: LoadState,
  page: number,
  onOpenDetail: (id: string) => void,
  onPageChange: (next: number) => void
) {
  if (state.kind === "loading") {
    return <p className="muted">Loading receipts…</p>;
  }
  if (state.kind === "error") {
    if (state.code === "receipt_storage_not_configured") {
      return (
        <div className="error">
          Receipt storage is not yet configured for this environment. Receipts will appear here once
          the storage layer is enabled.
        </div>
      );
    }
    return <div className="error">{state.message || "Could not load receipts."}</div>;
  }
  if (state.total === 0) {
    // Genuine empty library: the customer has never produced a receipt.
    // No pagination control because there is nothing to paginate.
    return (
      <p className="empty-state">
        No receipts yet. They will appear here once your SDK produces them.
      </p>
    );
  }
  if (state.receipts.length === 0) {
    // The total is non-zero but this page slice is empty: the caller
    // navigated to a page past the end (e.g. /receipts?page=999 on an
    // account with 50 receipts). Render the pagination so they can
    // jump back to a valid page; do not show the "no receipts yet"
    // copy, which would mis-describe the state.
    return (
      <>
        <p className="empty-state">
          This page is past the end of the list. Use the controls below to navigate to a valid page.
        </p>
        <Pagination
          currentPage={page}
          totalItems={state.total}
          pageSize={PAGE_SIZE}
          onPageChange={onPageChange}
        />
      </>
    );
  }
  return (
    <>
      <table className="data-table receipts-table">
        <thead>
          <tr>
            <th scope="col">Receipt</th>
            <th scope="col">Chain</th>
            <th scope="col" className="numeric">
              Actions
            </th>
            <th scope="col">Schema</th>
            <th scope="col">Created</th>
            <th scope="col">Envelope</th>
          </tr>
        </thead>
        <tbody>
          {state.receipts.map((row) => (
            <ReceiptRow key={row.receipt_id} row={row} onOpen={onOpenDetail} />
          ))}
        </tbody>
      </table>
      <Pagination
        currentPage={page}
        totalItems={state.total}
        pageSize={PAGE_SIZE}
        onPageChange={onPageChange}
      />
    </>
  );
}

function ReceiptRow(props: { row: ReceiptSummary; onOpen: (id: string) => void }) {
  const { row, onOpen } = props;
  return (
    <tr
      className="data-row data-row-clickable"
      onClick={() => onOpen(row.receipt_id)}
      tabIndex={0}
      role="link"
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onOpen(row.receipt_id);
        }
      }}
    >
      <td>
        <a
          href={receiptDetailPath(row.receipt_id)}
          className="receipt-link"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onOpen(row.receipt_id);
          }}
        >
          <code>{row.receipt_id}</code>
        </a>
      </td>
      <td>
        <code className="muted-code">{row.chain_id}</code>
      </td>
      <td className="numeric">
        {row.final_action_count !== null ? row.final_action_count : "n/a"}
      </td>
      <td>
        <code className="muted-code">{row.envelope_schema_version}</code>
      </td>
      <td title={row.created_at}>{formatTimestamp(row.created_at)}</td>
      <td>
        {row.envelope_stored ? (
          <span className="badge badge-live">stored</span>
        ) : (
          <span className="badge badge-test">customer-held</span>
        )}
      </td>
    </tr>
  );
}

function formatTimestamp(iso: string): string {
  const parsed = Date.parse(iso);
  if (Number.isNaN(parsed)) return iso;
  const date = new Date(parsed);
  return date.toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit"
  });
}
